import api from '@mint-lib/api';
import { useMutation, useQueryClient } from '@mint-lib/query-client';
import { getRouteApi } from '@mint/routing';

import { QueryKey } from '../constants/queryKeys.js';
import {
  CustomColumnSchema,
  type BuilderSchemaResponse,
  type CreateCustomTechCostColumnPayload,
  type EditCustomTechCostColumnPayload,
} from '../types/builderSchema.js';

const createCustomTechCostColumn = async (
  payload: CreateCustomTechCostColumnPayload & {
    campaignId: number;
  },
) => {
  const { campaignId, ...restData } = payload;
  const response = await api.post('api/datintell/v1/builder/custom_columns/', {
    campaignCollectionId: payload.campaignId,
    customField: {
      ...restData,
      dimension: 'tech_costs',
    },
  });

  const data = CustomColumnSchema.parse(response.data);
  return { ...data, autoTechCostPercent: payload.autoTechCostPercent };
};

const editCustomTechCostColumn = async (
  payload: EditCustomTechCostColumnPayload,
) => {
  const { id, ...restData } = payload;
  try {
    await api.put(`api/datintell/v1/builder/custom_columns/${id}/`, restData);
    return { name: id, ...restData };
  } catch {
    return Promise.reject(new Error('Failed to update custom column'));
  }
};

const deleteCustomTechCostColumn = async (id: string) => {
  try {
    await api.delete(`api/datintell/v1/builder/custom_columns/${id}/`);
    return { id };
  } catch {
    return Promise.reject(new Error('Failed to delete custom column'));
  }
};

const routeApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan',
);

export const useCreateCustomTechCostColumn = () => {
  const params = routeApi.useParams();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (payload: CreateCustomTechCostColumnPayload) =>
      createCustomTechCostColumn({
        ...payload,
        campaignId: Number(params.campaignId),
      }),
    onSuccess: (newColumn) => {
      queryClient.setQueryData<BuilderSchemaResponse>(
        [QueryKey.BUILDER_SCHEMA, params.campaignId],
        (oldData) => {
          const techCostColumn = {
            label: newColumn.label,
            autoTechCostPercent: newColumn.autoTechCostPercent,
            name: String(newColumn.id),
          };

          return oldData
            ? {
                ...oldData,
                otherTechCostFields: [
                  ...oldData.otherTechCostFields,
                  techCostColumn,
                ],
              }
            : oldData;
        },
      );
    },
  });

  return mutation.mutateAsync;
};

export const useEditCustomTechCostColumn = () => {
  const params = routeApi.useParams();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (payload: EditCustomTechCostColumnPayload) =>
      editCustomTechCostColumn(payload),
    onSuccess: (newColumn) => {
      queryClient.setQueryData<BuilderSchemaResponse>(
        [QueryKey.BUILDER_SCHEMA, params.campaignId],
        (oldData) => {
          return oldData
            ? {
                ...oldData,
                otherTechCostFields: oldData.otherTechCostFields.map(
                  (column) => {
                    return column.name === newColumn.name ? newColumn : column;
                  },
                ),
              }
            : oldData;
        },
      );
    },
  });

  return mutation.mutateAsync;
};

export const useDeleteCustomTechCostColumn = () => {
  const params = routeApi.useParams();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (columnId: string) => deleteCustomTechCostColumn(columnId),
    onSuccess: (newColumn) => {
      queryClient.setQueryData<BuilderSchemaResponse>(
        [QueryKey.BUILDER_SCHEMA, params.campaignId],
        (oldData) => {
          return oldData
            ? {
                ...oldData,
                otherTechCostFields: oldData.otherTechCostFields.filter(
                  (column) => column.name !== newColumn.id,
                ),
              }
            : oldData;
        },
      );
    },
  });

  return mutation.mutateAsync;
};
